/**
 * Performance monitoring utilities
 * Tracks Web Vitals and custom metrics
 */

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

const metrics: PerformanceMetric[] = [];

/**
 * Rate a metric value based on Web Vitals thresholds
 */
function rateMetric(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, { good: number; poor: number }> = {
    'FCP': { good: 1800, poor: 3000 },       // First Contentful Paint
    'LCP': { good: 2500, poor: 4000 },       // Largest Contentful Paint
    'FID': { good: 100, poor: 300 },         // First Input Delay
    'CLS': { good: 0.1, poor: 0.25 },        // Cumulative Layout Shift
    'TTFB': { good: 800, poor: 1800 },       // Time to First Byte
    'INP': { good: 200, poor: 500 },         // Interaction to Next Paint
  };

  const threshold = thresholds[name];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Record a performance metric
 */
export function recordMetric(name: string, value: number): void {
  const metric: PerformanceMetric = {
    name,
    value,
    rating: rateMetric(name, value),
    timestamp: Date.now(),
  };

  metrics.push(metric);

  // Keep only last 100 metrics
  if (metrics.length > 100) {
    metrics.shift();
  }

  // Log in development
  if (import.meta.env.DEV) {
    console.log(`[Performance] ${name}: ${value.toFixed(2)}ms (${metric.rating})`);
  }

  // Send to monitoring service in production
  if (!import.meta.env.DEV) {
    sendToMonitoring(metric);
  }
}

/**
 * Get all recorded metrics
 */
export function getMetrics(): PerformanceMetric[] {
  return [...metrics];
}

/**
 * Get metrics summary
 */
export function getMetricsSummary(): Record<string, {
  count: number;
  avg: number;
  min: number;
  max: number;
  good: number;
  needsImprovement: number;
  poor: number;
}> {
  const summary: Record<string, any> = {};

  metrics.forEach(metric => {
    if (!summary[metric.name]) {
      summary[metric.name] = {
        values: [],
        ratings: { good: 0, 'needs-improvement': 0, poor: 0 },
      };
    }

    summary[metric.name].values.push(metric.value);
    summary[metric.name].ratings[metric.rating]++;
  });

  return Object.entries(summary).reduce((acc, [name, data]) => {
    const values = data.values as number[];
    acc[name] = {
      count: values.length,
      avg: values.reduce((a, b) => a + b, 0) / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      good: data.ratings.good,
      needsImprovement: data.ratings['needs-improvement'],
      poor: data.ratings.poor,
    };
    return acc;
  }, {} as Record<string, any>);
}

/**
 * Clear all metrics
 */
export function clearMetrics(): void {
  metrics.length = 0;
}

/**
 * Send metric to monitoring service
 */
function sendToMonitoring(metric: PerformanceMetric): void {
  // TODO: Integrate with monitoring service (e.g., Vercel Analytics, Sentry)
  // For now, just log to console in production
  if (metric.rating === 'poor') {
    console.warn(`[Performance Warning] ${metric.name}: ${metric.value.toFixed(2)}ms`);
  }
}

/**
 * Measure function execution time
 */
export function measureAsync<T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> {
  const start = performance.now();
  return fn().finally(() => {
    const duration = performance.now() - start;
    recordMetric(name, duration);
  });
}

/**
 * Measure synchronous function execution time
 */
export function measure<T>(name: string, fn: () => T): T {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;
  recordMetric(name, duration);
  return result;
}

/**
 * Mark a custom timing point
 */
export function mark(name: string): void {
  performance.mark(name);
}

/**
 * Measure between two marks
 */
export function measureBetween(name: string, startMark: string, endMark: string): void {
  try {
    performance.measure(name, startMark, endMark);
    const measure = performance.getEntriesByName(name, 'measure')[0];
    if (measure) {
      recordMetric(name, measure.duration);
    }
  } catch {
    // Marks don't exist, ignore
  }
}

/**
 * Initialize Web Vitals monitoring
 */
export function initWebVitals(): void {
  // Only in browser
  if (typeof window === 'undefined') return;

  // Observe First Contentful Paint (FCP)
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === 'first-contentful-paint') {
        recordMetric('FCP', entry.startTime);
      }
    }
  });
  observer.observe({ entryTypes: ['paint'] });

  // Observe Largest Contentful Paint (LCP)
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    recordMetric('LCP', lastEntry.startTime);
  });
  lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

  // Observe Cumulative Layout Shift (CLS)
  let clsScore = 0;
  const clsObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!(entry as any).hadRecentInput) {
        clsScore += (entry as any).value;
        recordMetric('CLS', clsScore);
      }
    }
  });
  clsObserver.observe({ entryTypes: ['layout-shift'] });

  // Observe First Input Delay (FID)
  const fidObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      recordMetric('FID', (entry as any).processingStart - entry.startTime);
    }
  });
  fidObserver.observe({ entryTypes: ['first-input'] });

  // Measure Time to First Byte (TTFB)
  const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigationEntry) {
    recordMetric('TTFB', navigationEntry.responseStart - navigationEntry.requestStart);
  }
}
